/**
 * ProvisioningCard · Cruise preparation experience.
 *
 * PO directive (Cruise Auto-Provisioning · 2026-03-06):
 *   "Provisioning should feel like a concierge preparing a vacation.
 *    Not software configuring itself. Every screen should create
 *    confidence and anticipation."
 *
 * PO refinements (2026-07-13):
 *   1 · Personalise with cruise name + dates.
 *   2 · Ship hero image as the backdrop (gradient fallback).
 *   3 · Meaningful stage details from stage.result.detail.
 *   4 · Manual "Continue planning →" button — no auto-transition.
 *   5 · Subtle completion celebration (sparkle burst + shimmer).
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Check, Loader2, AlertTriangle, Sparkles, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const StageIcon = ({ status }) => {
  if (status === "completed") return <Check className="w-5 h-5 text-emerald-300" strokeWidth={3} />;
  if (status === "running")   return <Loader2 className="w-5 h-5 text-white animate-spin" />;
  if (status === "failed")    return <AlertTriangle className="w-5 h-5 text-amber-300" />;
  return <div className="w-2 h-2 rounded-full bg-white/30" />;
};

const formatDateRange = (a, b) => {
  const parse = (iso) => {
    if (!iso) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    if (!m) return null;
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  };
  const da = parse(a);
  const db = parse(b);
  if (!da || !db) return null;
  const short = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const yr = db.getFullYear();
  return `${short(da)} – ${short(db)}, ${yr}`;
};

/**
 * SparkleBurst — CSS-only celebration. Renders 14 sparkle particles
 * radiating from the centre with staggered timing. Zero dependencies.
 */
const SparkleBurst = () => {
  const particles = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const angle = (i / 14) * Math.PI * 2;
    const dist = 90 + (i % 3) * 26;
    return {
      key: i,
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      delay: (i % 7) * 60,
      size: 6 + (i % 3) * 3,
    };
  }), []);
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
      {particles.map((p) => (
        <span
          key={p.key}
          className="absolute rounded-full bg-[#FFD87A]"
          style={{
            width: p.size, height: p.size,
            filter: "drop-shadow(0 0 6px rgba(255,220,140,0.9))",
            transform: `translate(${p.x}px, ${p.y}px)`,
            opacity: 0,
            animation: `cc-sparkle 1600ms cubic-bezier(.16,1,.3,1) ${p.delay}ms 1 forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes cc-sparkle {
          0%   { opacity: 0; transform: translate(0,0) scale(0.4); }
          40%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--tx,0), var(--ty,0)) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default function ProvisioningCard({ cruiseId, onComplete }) {
  const [pipeline, setPipeline] = useState(null);
  const [starting, setStarting] = useState(true);
  const [celebrate, setCelebrate] = useState(false);

  const fetchProgress = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/cruises/${cruiseId}/provisioning`, { withCredentials: true });
      setPipeline(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, [cruiseId]);

  // Kick off on mount if not already started.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const existing = await fetchProgress();
      if (!mounted) return;
      if (!existing || existing.status === "not_started") {
        try {
          await axios.post(`${API}/api/cruises/${cruiseId}/provisioning/start`, {}, { withCredentials: true });
        } catch { /* silent · progress endpoint will catch up */ }
        // Refetch immediately so the pipeline transitions from
        // not_started → in_progress and the polling effect below
        // takes over. Without this the card stays frozen at 0/8.
        await fetchProgress();
      }
      setStarting(false);
    })();
    return () => { mounted = false; };
  }, [cruiseId, fetchProgress]);

  // Poll every 1.5s until the pipeline reaches a terminal state.
  useEffect(() => {
    if (!pipeline) return;
    const s = pipeline.status;
    if (s === "completed" || s === "partially_failed") return;
    const id = setInterval(fetchProgress, 1500);
    return () => clearInterval(id);
  }, [pipeline, fetchProgress]);

  // Fire the sparkle burst once when pipeline reaches its terminal state.
  const done = pipeline?.status === "completed" || pipeline?.status === "partially_failed";
  useEffect(() => {
    if (done && !celebrate) setCelebrate(true);
  }, [done, celebrate]);

  const retry = async (stageKey) => {
    try {
      await axios.post(`${API}/api/cruises/${cruiseId}/provisioning/${stageKey}/retry`, {}, { withCredentials: true });
      fetchProgress();
    } catch { /* silent */ }
  };

  const stages = pipeline?.stages || [];
  const cruise = pipeline?.cruise || {};
  const hero = pipeline?.hero_image || {};
  const heroUrl = hero.image_url && !hero.fallback_used ? hero.image_url : null;
  const cruiseName = cruise.name || "Your cruise";
  const dateRange = formatDateRange(cruise.embark_date, cruise.disembark_date);
  const shipName = cruise.ship_name || null;

  // Completed count for the progress track.
  const totalStages = stages.length || 8;
  const completedStages = stages.filter((s) => s.status === "completed").length;
  const progressPct = Math.min(100, Math.round((completedStages / totalStages) * 100));

  const backdrop = heroUrl
    ? {
        backgroundImage:
          `linear-gradient(135deg, rgba(6,26,57,0.82) 0%, rgba(15,82,198,0.55) 55%, rgba(6,26,57,0.72) 100%), url("${heroUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : { background: "linear-gradient(135deg, #061a39 0%, #0f52c6 55%, #3ea1ff 100%)" };

  return (
    <div
      data-testid="provisioning-card"
      className="fixed inset-0 z-50 flex items-center justify-center p-6 overflow-hidden"
      style={backdrop}
    >
      {celebrate && <SparkleBurst />}

      <div className="relative w-full max-w-2xl text-white">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          <div className="cc-overline text-[#D4AF37] text-[10px]">
            {done ? "Ready to plan" : "Preparing your cruise"}
          </div>
        </div>

        <h1
          className="font-display text-[38px] md:text-[44px] leading-[1.05] font-semibold"
          data-testid="provisioning-headline"
        >
          {done ? `${cruiseName} is ready.` : `Preparing ${cruiseName}…`}
        </h1>

        {(shipName || dateRange) && (
          <div className="mt-2 text-white/85 text-[14px] flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="provisioning-cruise-meta">
            {shipName && <span className="font-medium">{shipName}</span>}
            {shipName && dateRange && <span className="text-white/40">·</span>}
            {dateRange && <span>{dateRange}</span>}
          </div>
        )}

        <p className="text-white/80 text-[14px] mt-3 max-w-lg leading-relaxed">
          {done
            ? "Everything's in place. Your dashboard, itinerary and Cruise Binder are ready — you can start planning whenever you are."
            : "We're setting things up in the background so your dashboard, itinerary and Cruise Binder are all ready when you land."}
        </p>

        {/* Progress track */}
        <div className="mt-6 h-1.5 w-full bg-white/10 rounded-full overflow-hidden" data-testid="provisioning-progress-track">
          <div
            className="h-full rounded-full transition-[width] duration-700 ease-out"
            style={{
              width: `${progressPct}%`,
              background: "linear-gradient(90deg,#FFD87A,#3ea1ff)",
            }}
          />
        </div>
        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-white/60 font-semibold">
          {completedStages} of {totalStages} ready
        </div>

        <ul className="mt-6 space-y-2.5">
          {starting && !stages.length && (
            <li className="text-white/70 text-[13px] italic" data-testid="provisioning-starting">
              Gathering the details for your cruise…
            </li>
          )}
          {stages.map((s) => {
            const detail = s.result?.detail;
            const showDetail = s.status === "completed" && detail;
            return (
              <li
                key={s.key}
                className="flex items-start gap-3 py-0.5"
                data-testid={`provisioning-stage-${s.key}`}
              >
                <div className="mt-0.5 w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                  <StageIcon status={s.status} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[14px] ${
                    s.status === "completed" ? "text-white/85"
                    : s.status === "running" ? "text-white font-semibold"
                    : s.status === "failed" ? "text-amber-100 font-medium"
                    : "text-white/50"
                  }`}>
                    {s.label}
                  </div>
                  {showDetail && (
                    <div className="text-[12px] text-white/60 mt-0.5" data-testid={`provisioning-stage-${s.key}-detail`}>
                      {detail}
                    </div>
                  )}
                  {s.status === "failed" && s.last_error && (
                    <div className="text-[12px] text-amber-200/80 mt-0.5">
                      Couldn't finish — you can try again.
                    </div>
                  )}
                </div>
                {s.status === "failed" && (
                  <button
                    onClick={() => retry(s.key)}
                    className="text-[11px] uppercase tracking-[0.14em] font-semibold text-amber-200 hover:text-white underline"
                    data-testid={`provisioning-retry-${s.key}`}
                  >
                    Try again
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        {done && (
          <div className="mt-8" data-testid="provisioning-done-actions">
            <button
              type="button"
              onClick={() => onComplete?.()}
              className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-semibold text-[#0F172A] bg-white hover:bg-[#FFD87A] shadow-lg transition-colors"
              data-testid="provisioning-continue-btn"
              autoFocus
            >
              Continue planning
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <div className="mt-3 text-[12px] text-white/60">
              You'll land in your cruises so you can dive in wherever you like.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
